import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = {
  class: "filters-panel"
};
const _hoisted_2 = {
  class: "flex items-center gap-3 mb-2"
};
const _hoisted_3 = {
  key: 1,
  class: "text-sm text-green-600 bg-green-50 border px-2 py-1 rounded-md"
};
const _hoisted_4 = {
  class: "bg-green-50 p-4 rounded-md"
};
const _hoisted_5 = {
  class: "col-span-2"
};
const _hoisted_6 = {
  key: 0
};
const _hoisted_7 = {
  class: "advanced-filters-content"
};
const _hoisted_8 = {
  class: "grid grid-cols-4 gap-4 mb-2"
};
const _hoisted_9 = {
  key: 0,
  class: "grid grid-cols-4 gap-4 mb-2 mt-2"
};
const _hoisted_10 = {
  class: "flex justify-start mt-4"
};
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useCustomFieldsStore } from '@/modules/settings/company/CustomFields/useCustomFieldsStore';
import LlSelect from '@/components/LlSelect.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import DateRangeSelect from '@/components/DateRangeSelect.vue';
import LlBilledStatusSelect from '@/components/LlBilledStatusSelect.vue';
import LlPracticeAreaSelect from '@/components/LlPracticeAreaSelect.vue';
import ClientMatterSearch from '@/components/ClientMatterSearch.vue';
import TimekeeperPicker from '@/components/TimekeeperPicker.vue';
import MatterInvolvingFilter from '@/components/MatterInvolvingFilter.vue';
import EventTracker from '@/services/eventTracker';
import { TrackingCategory, TrackingAction, TrackingPageType, FilterType } from '@/constants/trackingConstants';
import { TIME_LOCK_FILTER, TIME_LOCK_FILTER_OPTIONS, BILLED_FILTER_VALUE } from './constants';
import { isTimeLockVisible } from './utils/fixedFeeLockGate';
import { useFixedFeesFilters } from './useFixedFeesFilters';
import { useFixedFeeDashboard } from './useFixedFeeDashboard';
export default {
  __name: 'FixedFeesFilters',
  props: {
    tracking: {
      type: Object,
      required: false
    }
  },
  emits: ['apply-filters', 'clear-filters', 'create-fixed-fee'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;

    // Stores
    const meStore = useMeStore();
    const {
      me,
      features,
      company
    } = storeToRefs(meStore);

    // Same gate as the column, so the filter and the thing it filters can never
    // disagree — see utils/fixedFeeLockGate.
    const showTimeLockFilter = computed(() => isTimeLockVisible({
      features: features.value,
      companySettings: company.value?.settings
    }));
    const timeLockFilterOptions = TIME_LOCK_FILTER_OPTIONS;
    const accountsStore = useAccountsStore();
    const {
      accountsList
    } = storeToRefs(accountsStore);
    const customFieldsStore = useCustomFieldsStore();
    const {
      customFields
    } = storeToRefs(customFieldsStore);

    // Filter Composable
    const {
      filters,
      completionStateOptions,
      chargeTypeOptions,
      activeFilterCount,
      resetFilters
    } = useFixedFeesFilters();

    // Dashboard Composable
    const {
      toggleInsightsVisibility
    } = useFixedFeeDashboard();

    // A Time Lock status only means anything on a billed fee, so picking one
    // narrows Billed Status with it (UI-015, BR-020). Clearing back to All leaves
    // Billed Status alone — the user may still want it there (BR-014).
    //
    // Known limit, kept deliberately: `billed` is `final` OR `interim` and
    // BilledState holds one value, so this hides fees billed onto a draft invoice.
    // Reopening Billed Status to All after choosing a lock value shows them again.
    const handleTimeLockChange = value => {
      if (value && value !== TIME_LOCK_FILTER.ALL) {
        filters.value.billedState = BILLED_FILTER_VALUE;
      }
    };

    // ── Debounced Filter Tracking ──────────────────────────────────────────
    // Per-filter-type timers so rapid changes to the SAME filter produce
    // only one "Filter Applied" event after 300 ms of idle.
    const filterTimers = {};
    const trackFilterApplied = (filterType, filterValue) => {
      if (filterTimers[filterType]) {
        clearTimeout(filterTimers[filterType]);
      }
      filterTimers[filterType] = setTimeout(() => {
        if (props.tracking) {
          EventTracker.trackEvent(props.tracking, TrackingCategory.FIXED_FEE_LIST, TrackingAction.FILTER_APPLIED, {
            filterType,
            filterValue: filterValue ?? 'none',
            pageType: TrackingPageType.FIXED_FEE_LIST
          });
        }
        delete filterTimers[filterType];
      }, 300);
    };

    // Clean up pending timers when the component unmounts
    onBeforeUnmount(() => {
      Object.values(filterTimers).forEach(clearTimeout);
    });

    // UI State
    const showFilters = ref(true);
    const showAdvancedFilters = ref(false);
    const billableDateCustom = ref(false);

    // Billable Date takes two columns while showing a custom range, and the Time
    // Lock filter only exists for an opted-in firm.
    //
    // Spelled out rather than interpolated: Tailwind generates only the class names
    // it finds literally in the source, so `grid-cols-${n}` compiles to nothing it
    // has seen. Same lookup Time.vue uses for the same reason.
    const PRIMARY_FILTER_GRID_CLASSES = {
      4: 'grid-cols-4',
      5: 'grid-cols-5',
      6: 'grid-cols-6'
    };
    const primaryFilterGridClass = computed(() => {
      const columns = 4 + (billableDateCustom.value ? 1 : 0) + (showTimeLockFilter.value ? 1 : 0);
      return PRIMARY_FILTER_GRID_CLASSES[columns] || 'grid-cols-4';
    });
    const filterResetKey = ref(0); // Key to force DateRangeSelect reset
    const lockAccountSelection = ref(false);

    // Custom field filters (separate arrays for client and matter fields)
    const clientCustomFieldFilters = ref([]);
    const matterCustomFieldFilters = ref([]);

    // SC-43245: Count of filters the user CAN clear (excludes locked/permission-based filters).
    // Used to hide the "Clear All" button when only locked filters remain.
    const clearableFilterCount = computed(() => {
      if (!lockAccountSelection.value) return activeFilterCount.value;
      // Subtract 1 for the locked scopeAccount filter when it's counted
      return Math.max(0, activeFilterCount.value - (filters.value.scopeAccount ? 1 : 0));
    });

    // Hide matters involving filter for operators and accountants (they see all matters)
    const showMatterInvolvingFilter = computed(() => {
      const role = me.value?.role;
      return role && role !== 'operator' && role !== 'accountant';
    });

    // Set default matters involving filter on mount (same as matters/expenses pages)
    onMounted(async () => {
      // Wait for me data to be available
      if (!me.value) {
        await meStore.getMe();
      }

      // Load accounts list if not already loaded
      if (!accountsList.value || accountsList.value.length === 0) {
        await accountsStore.listAccounts();
      }

      // Load custom fields if feature is enabled
      if (me.value?.features?.customFieldFilters) {
        await loadCustomFields();
      }

      // Set default matters involving based on role
      if (me.value?.id && accountsList.value) {
        const role = me.value.role;
        const hasAutoAssign = me.value.permissions?.matters?.autoAssign;

        // Lock account selection for lead, timekeeper, paralegal unless they have autoAssign permission
        const lockByDefault = role === 'lead' || role === 'timekeeper' || role === 'paralegal';
        lockAccountSelection.value = lockByDefault && !hasAutoAssign;

        // Set default scopeAccount for non-operator/accountant roles
        if (role !== 'operator' && role !== 'accountant') {
          const currentUserAccount = accountsList.value.find(acc => acc.id === me.value.id);
          if (currentUserAccount) {
            filters.value.scopeAccount = currentUserAccount;
            // Set primary (Responsible) by default for all non-operator/accountant roles
            filters.value.primary = true;

            // Set involved to false since primary is selected
            filters.value.involved = false;

            // Emit the filters to apply them on initial load
            emit('apply-filters', filters.value);
          }
        }
      }
    });

    // Query filter for ClientMatterSearch - reflects matters involving filter
    const cmQueryFilter = computed(() => ({
      scopeAccount: filters.value.scopeAccount?.id || null,
      originating: filters.value.originating,
      involved: filters.value.involved,
      reviewer: filters.value.reviewer,
      primary: filters.value.primary,
      archived: false
    }));

    // Methods
    const toggleFilters = () => {
      showFilters.value = !showFilters.value;
      emit('filters-visibility-changed', showFilters.value);
    };
    const toggleInsights = () => {
      toggleInsightsVisibility();
    };
    const toggleAdvancedFilters = () => {
      showAdvancedFilters.value = !showAdvancedFilters.value;
    };
    const handleClientMatterChange = () => {
      // Update filters based on client/matter selection from cm object
      filters.value.clientId = filters.value.cm?.client?.id || null;
      filters.value.matterId = filters.value.cm?.matter?.id || null;
      emit('apply-filters', filters.value);
      const clientName = filters.value.cm?.client?.name;
      const matterName = filters.value.cm?.matter?.name;
      const label = [clientName, matterName].filter(Boolean).join(' / ');
      trackFilterApplied(FilterType.CLIENT_MATTER, label || 'none');
    };
    const handleAccountChange = updatedForm => {
      filters.value.account = updatedForm.account;
      filters.value.invoiceUserId = updatedForm.account?.id || null;
      emit('apply-filters', filters.value);
      trackFilterApplied(FilterType.INVOICE_USER, updatedForm.account?.name || 'none');
    };
    const handleMatterInvolvingChange = updatedForm => {
      // Update all matters involving filter fields
      filters.value.scopeAccount = updatedForm.scopeAccount;
      filters.value.originating = updatedForm.originating || false;
      filters.value.primary = updatedForm.primary || false;
      filters.value.reviewer = updatedForm.reviewer || false;

      // Set involved based on whether any specific role is selected
      // If no specific role selected but scopeAccount exists, default to involved=true
      if (filters.value.scopeAccount) {
        const hasSpecificRole = filters.value.originating || filters.value.primary || filters.value.reviewer;
        filters.value.involved = !hasSpecificRole;
      } else {
        filters.value.involved = false;
      }

      // Clear client/matter selection when matters involving filter changes
      // This forces ClientMatterSearch to reload with new filter constraints
      if (filters.value.cm) {
        filters.value.cm.client = null;
        filters.value.cm.matter = null;
      }
      filters.value.clientId = null;
      filters.value.matterId = null;
      emit('apply-filters', filters.value);
      trackFilterApplied(FilterType.RESPONSIBLE_ATTORNEY, updatedForm.scopeAccount?.name || 'none');
    };
    const handleBillableDateRange = dateSelect => {
      filters.value.billableDateStart = dateSelect.startDate;
      filters.value.billableDateEnd = dateSelect.endDate;
      emit('apply-filters', filters.value);
      const range = [dateSelect.startDate, dateSelect.endDate].filter(Boolean).join(' – ');
      trackFilterApplied(FilterType.BILLABLE_DATE, range || 'all-time');
    };

    // Guard flag: when true, the v-model watcher skips individual tracking
    // so only one "Clear All" event is emitted.
    let isClearingAll = false;
    const handleClearFilters = () => {
      isClearingAll = true;
      resetFilters();
      // Clear client/matter selection
      if (filters.value.cm) {
        filters.value.cm.client = null;
        filters.value.cm.matter = null;
      }

      // Clear custom field filters
      filters.value.clientCustomFields = {};
      filters.value.matterCustomFields = {};

      // SC-43245: Only clear matters-involving filters when they are NOT locked.
      // For locked users (timekeeper/lead/paralegal without autoAssign) re-derive
      // the permission-scoped defaults from the source of truth rather than
      // saving/restoring, so the locked state is always consistent.
      if (lockAccountSelection.value) {
        const currentUser = accountsList.value?.find(acc => acc.id === me.value?.id);
        if (currentUser) {
          filters.value.scopeAccount = currentUser;
          filters.value.primary = true;
        }
        filters.value.originating = false;
        filters.value.involved = false;
        filters.value.reviewer = false;
      } else {
        filters.value.scopeAccount = null;
        filters.value.originating = false;
        filters.value.involved = false;
        filters.value.primary = false;
        filters.value.reviewer = false;
      }
      // Reset to preset mode (hide custom date inputs)
      billableDateCustom.value = false;
      // Force DateRangeSelect to re-mount and reset to "All Time"
      filterResetKey.value++;
      emit('clear-filters');

      // Cancel any pending per-filter debounce timers
      Object.values(filterTimers).forEach(clearTimeout);
      Object.keys(filterTimers).forEach(k => delete filterTimers[k]);

      // Fire a single "Clear All" event
      trackFilterApplied(FilterType.CLEAR_ALL, 'all');

      // Reset guard after watchers have drained (they run on nextTick)
      nextTick(() => {
        isClearingAll = false;
      });
    };
    const handleCreateFixedFee = () => {
      emit('create-fixed-fee');
    };

    // Load custom fields and filter for active enum fields
    const loadCustomFields = async () => {
      try {
        await customFieldsStore.listCustomFields();
        const fields = customFields.value || [];

        // Filter for client dropdown/enum fields that are active
        clientCustomFieldFilters.value = fields.filter(f => f.entity === 'client' && f.type === 'enum' && f.active);

        // Filter for matter dropdown/enum fields that are active
        matterCustomFieldFilters.value = fields.filter(f => f.entity === 'matter' && f.type === 'enum' && f.active);
      } catch (error) {
        console.error('Error loading custom fields:', error);
      }
    };

    // Handle custom field filter changes
    const handleCustomFieldChanged = (entity, fieldId, value) => {
      if (entity === 'client') {
        if (!filters.value.clientCustomFields) {
          filters.value.clientCustomFields = {};
        }
        if (value && value !== '') {
          filters.value.clientCustomFields[fieldId] = value;
        } else {
          delete filters.value.clientCustomFields[fieldId];
        }
      } else if (entity === 'matter') {
        if (!filters.value.matterCustomFields) {
          filters.value.matterCustomFields = {};
        }
        if (value && value !== '') {
          filters.value.matterCustomFields[fieldId] = value;
        } else {
          delete filters.value.matterCustomFields[fieldId];
        }
      }
      emit('apply-filters', filters.value);

      // Track custom field filter application
      const customField = entity === 'client' ? clientCustomFieldFilters.value.find(f => f.id === fieldId) : matterCustomFieldFilters.value.find(f => f.id === fieldId);
      const fieldName = customField ? customField.name : fieldId;
      trackFilterApplied(FilterType.CUSTOM_FIELD, `${entity}: ${fieldName}`);
    };

    // Anything listed here re-queries the table when it changes. A filter left out
    // only appears to work when something else in the list happens to change with it.
    const TRACKED_VMODEL_FILTERS = {
      completionState: FilterType.COMPLETION_STATUS,
      chargeType: FilterType.FEE_TYPE,
      billedState: FilterType.BILLED_STATUS,
      timeLockState: FilterType.TIME_LOCK_STATUS,
      practiceAreaId: FilterType.PRACTICE_AREA
    };
    // Single watcher: emits filter update AND tracks which value(s) changed.
    watch(() => Object.keys(TRACKED_VMODEL_FILTERS).map(k => filters.value[k]), (newVals, oldVals) => {
      emit('apply-filters', filters.value);

      // Skip individual tracking when "Clear All" triggered the reset
      if (isClearingAll) return;

      // Diff old vs new to fire tracking only for the filter(s) that changed
      const keys = Object.keys(TRACKED_VMODEL_FILTERS);
      keys.forEach((key, i) => {
        if (newVals[i] !== oldVals?.[i]) {
          trackFilterApplied(TRACKED_VMODEL_FILTERS[key], newVals[i]);
        }
      });
    });
    return (_ctx, _cache) => {
      const _component_v_spacer = _resolveComponent("v-spacer");
      const _component_v_icon = _resolveComponent("v-icon");
      const _component_v_expand_transition = _resolveComponent("v-expand-transition");
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, [_createVNode(LlSecondaryButton, {
        variant: "outlined",
        size: "small",
        "prepend-icon": "mdi-filter-variant",
        onClick: toggleFilters
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [_createTextVNode(" Filters ", -1)]))]),
        _: 1
      }), _unref(features)?.feesMetabaseIntegration ? (_openBlock(), _createBlock(LlSecondaryButton, {
        key: 0,
        variant: "outlined",
        size: "small",
        "append-icon": "mdi-chart-timeline-variant-shimmer",
        onClick: toggleInsights
      }, {
        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [_createTextVNode(" Insights ", -1)]))]),
        _: 1
      })) : _createCommentVNode("", true), _unref(activeFilterCount) > 0 ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(activeFilterCount)) + " filters are active ", 1)) : _createCommentVNode("", true), clearableFilterCount.value > 0 ? (_openBlock(), _createBlock(LlSecondaryButton, {
        key: 2,
        variant: "text",
        size: "small",
        color: "error",
        "prepend-icon": "mdi-close",
        onClick: handleClearFilters
      }, {
        default: _withCtx(() => [...(_cache[8] || (_cache[8] = [_createTextVNode(" Clear All ", -1)]))]),
        _: 1
      })) : _createCommentVNode("", true), _createVNode(_component_v_spacer), _createVNode(LlSecondaryButton, {
        color: "primary",
        variant: "flat",
        onClick: handleCreateFixedFee,
        "data-cy": "create-fixed-fee-btn"
      }, {
        default: _withCtx(() => [_createVNode(_component_v_icon, {
          class: "mr-1"
        }, {
          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [_createTextVNode("mdi-plus", -1)]))]),
          _: 1
        }), _cache[10] || (_cache[10] = _createTextVNode(" Create Fixed Fee ", -1))]),
        _: 1
      })]), _withDirectives(_createElementVNode("div", _hoisted_4, [_createElementVNode("div", {
        class: _normalizeClass(["grid gap-4 mb-2", primaryFilterGridClass.value])
      }, [_createElementVNode("div", {
        class: _normalizeClass({
          'col-span-2': billableDateCustom.value
        })
      }, [(_openBlock(), _createBlock(DateRangeSelect, {
        key: filterResetKey.value,
        "default-range": 'Billable Date',
        layout: billableDateCustom.value ? 'horizontal' : 'vertical',
        onDateRangeFilter: handleBillableDateRange,
        onCustomPeriodToggle: _cache[0] || (_cache[0] = $event => billableDateCustom.value = $event),
        "data-cy": "filter-billable-date-range"
      }, null, 8, ["layout"]))], 2), _createElementVNode("div", _hoisted_5, [_createVNode(ClientMatterSearch, {
        cm: _unref(filters).cm,
        "query-filter": cmQueryFilter.value,
        horizontal: false,
        emitOnAnyChange: true,
        onMatterChange: handleClientMatterChange,
        "data-cy": "filter-client-matter"
      }, null, 8, ["cm", "query-filter"])]), _createElementVNode("div", null, [_createVNode(LlBilledStatusSelect, {
        modelValue: _unref(filters).billedState,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => _unref(filters).billedState = $event),
        "return-object": false,
        placeholder: "All billed status",
        "label-class": "block text-sm mb-1",
        "data-cy": "filter-billed-status"
      }, null, 8, ["modelValue"])]), showTimeLockFilter.value ? (_openBlock(), _createElementBlock("div", _hoisted_6, [_createVNode(LlSelect, {
        modelValue: _unref(filters).timeLockState,
        "onUpdate:modelValue": [_cache[2] || (_cache[2] = $event => _unref(filters).timeLockState = $event), handleTimeLockChange],
        items: _unref(timeLockFilterOptions),
        label: "Time Lock Status",
        "item-title": "label",
        "item-value": "id",
        "return-object": false,
        "hide-details": "true",
        isAutocomplete: "",
        placeholder: "All time lock status",
        labelClass: "py-1 block text-sm mb-1",
        "data-cy": "filter-time-lock-status"
      }, null, 8, ["modelValue", "items"])])) : _createCommentVNode("", true)], 2), _createVNode(_component_v_expand_transition, null, {
        default: _withCtx(() => [_withDirectives(_createElementVNode("div", _hoisted_7, [_createElementVNode("div", _hoisted_8, [_createElementVNode("div", null, [_createVNode(TimekeeperPicker, {
          form: _unref(filters),
          label: "Invoice User",
          labelClass: "block text-sm py-1",
          clearable: true,
          disabled: false,
          "onUpdate:form": handleAccountChange,
          "data-cy": "filter-invoice-user"
        }, null, 8, ["form"])]), _createElementVNode("div", null, [_createVNode(LlPracticeAreaSelect, {
          modelValue: _unref(filters).practiceAreaId,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => _unref(filters).practiceAreaId = $event),
          "return-object": false,
          "label-class": "block text-sm mb-1",
          placeholder: "All",
          clearable: true,
          searchable: true,
          "data-cy": "filter-practice-area"
        }, null, 8, ["modelValue"])]), _createElementVNode("div", null, [_createVNode(LlSelect, {
          modelValue: _unref(filters).chargeType,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => _unref(filters).chargeType = $event),
          items: _unref(chargeTypeOptions),
          "item-title": "title",
          "item-value": "value",
          "return-object": false,
          density: "compact",
          placeholder: "All Fee Type",
          variant: "outlined",
          label: "Fee Type",
          "hide-details": "",
          "is-autocomplete": true,
          clearable: "",
          labelClass: "py-1 text-sm",
          "data-cy": "filter-fee-type"
        }, null, 8, ["modelValue", "items"])]), _createElementVNode("div", null, [_createVNode(LlSelect, {
          modelValue: _unref(filters).completionState,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => _unref(filters).completionState = $event),
          items: _unref(completionStateOptions),
          "item-title": "title",
          "item-value": "value",
          "return-object": false,
          density: "compact",
          placeholder: "all",
          variant: "outlined",
          "hide-details": "",
          label: "Completion Status",
          "is-autocomplete": true,
          labelClass: "py-1 text-sm",
          "data-cy": "filter-completion-status"
        }, null, 8, ["modelValue", "items"])]), showMatterInvolvingFilter.value ? (_openBlock(), _createBlock(MatterInvolvingFilter, {
          key: 0,
          form: _unref(filters),
          lockAccountSelection: lockAccountSelection.value,
          simplifiedMode: true,
          onMatterInvolvingChange: handleMatterInvolvingChange,
          "data-cy": "filter-matters-involving"
        }, null, 8, ["form", "lockAccountSelection"])) : _createCommentVNode("", true)]), _unref(features)?.customFieldFilters && (clientCustomFieldFilters.value.length > 0 || matterCustomFieldFilters.value.length > 0) ? (_openBlock(), _createElementBlock("div", _hoisted_9, [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(clientCustomFieldFilters.value, customField => {
          return _openBlock(), _createElementBlock("div", {
            key: `client-${customField.id}`
          }, [_createVNode(LlSelect, {
            "model-value": _unref(filters).clientCustomFields?.[customField.id],
            "onUpdate:modelValue": $event => handleCustomFieldChanged('client', customField.id, $event),
            items: [{
              label: 'Not assigned',
              value: 'NOT_ASSIGNED'
            }, ...(customField.enumOptions || []).map(opt => ({
              label: opt.value || opt.label,
              value: opt.id
            }))],
            "item-title": "label",
            "item-value": "value",
            "return-object": false,
            density: "compact",
            placeholder: `All ${customField.name}`,
            variant: "outlined",
            label: `Client: ${customField.name}`,
            "hide-details": "",
            "is-autocomplete": true,
            clearable: "",
            labelClass: "py-1 text-sm",
            "data-cy": `filter-client-custom-field-${customField.id}`
          }, null, 8, ["model-value", "onUpdate:modelValue", "items", "placeholder", "label", "data-cy"])]);
        }), 128)), (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(matterCustomFieldFilters.value, customField => {
          return _openBlock(), _createElementBlock("div", {
            key: `matter-${customField.id}`
          }, [_createVNode(LlSelect, {
            "model-value": _unref(filters).matterCustomFields?.[customField.id],
            "onUpdate:modelValue": $event => handleCustomFieldChanged('matter', customField.id, $event),
            items: [{
              label: 'Not assigned',
              value: 'NOT_ASSIGNED'
            }, ...(customField.enumOptions || []).map(opt => ({
              label: opt.value || opt.label,
              value: opt.id
            }))],
            "item-title": "label",
            "item-value": "value",
            "return-object": false,
            density: "compact",
            placeholder: `All ${customField.name}`,
            variant: "outlined",
            label: `Matter: ${customField.name}`,
            "hide-details": "",
            "is-autocomplete": true,
            clearable: "",
            labelClass: "py-1 text-sm",
            "data-cy": `filter-matter-custom-field-${customField.id}`
          }, null, 8, ["model-value", "onUpdate:modelValue", "items", "placeholder", "label", "data-cy"])]);
        }), 128))])) : _createCommentVNode("", true)], 512), [[_vShow, showAdvancedFilters.value]])]),
        _: 1
      }), _createElementVNode("div", _hoisted_10, [_createVNode(LlSecondaryButton, {
        variant: "outlined",
        color: "primary",
        size: "small",
        onClick: toggleAdvancedFilters
      }, {
        default: _withCtx(() => [_createTextVNode(_toDisplayString(showAdvancedFilters.value ? 'Hide' : 'Show') + " Advanced Filters ", 1)]),
        _: 1
      })])], 512), [[_vShow, showFilters.value]])]);
    };
  }
};